import { useState, useEffect } from 'react';
import { ENDPOINTS } from '../config';
import { getHomeAccessories } from '../api/product.api';
import { getCacheEntry, setCacheEntry } from '../utils/fetchCache';
import { flushPendingScrollRestore } from '../utils/scrollRestoration';

const HOME_CACHE_KEY = 'home-page-data';
/** 5 minutes — instant back-navigation; refresh for newer CMS content */
const HOME_TTL_MS = 5 * 60 * 1000;

async function fetchCoreHome() {
    const [secRes, banRes] = await Promise.all([
        fetch(ENDPOINTS.HOME_SECTIONS.PUBLIC),
        fetch(ENDPOINTS.BANNERS.PUBLIC),
    ]);

    const secData = await secRes.json();
    const banData = await banRes.json();

    const sections =
        secData.isOk && secData.data
            ? [...secData.data].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
            : [];

    const banners = banData.isOk && banData.data ? banData.data : [];

    return { sections, banners };
}

async function fetchAccessoriesSafe() {
    try {
        const accRes = await getHomeAccessories();
        if (!accRes?.data?.isOk) return { items: [], types: [] };
        const data = accRes.data.data || {};
        return {
            items: data.items || [],
            types: data.types || [],
        };
    } catch (err) {
        console.error('Error fetching home accessories:', err);
        return { items: [], types: [] };
    }
}

/**
 * Homepage data with in-memory cache (stale-while-revalidate).
 * Sections + banners paint first; accessories load without blocking hero/CMS.
 */
export function useHomePageData() {
    const cached = getCacheEntry(HOME_CACHE_KEY, HOME_TTL_MS);

    const [sections, setSections] = useState(cached?.sections ?? []);
    const [banners, setBanners] = useState(cached?.banners ?? []);
    const [accessories, setAccessories] = useState(cached?.accessories ?? []);
    const [accessoryTypes, setAccessoryTypes] = useState(cached?.accessoryTypes ?? []);
    const [loading, setLoading] = useState(!cached);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            const hadCache = !!getCacheEntry(HOME_CACHE_KEY, HOME_TTL_MS);
            if (!hadCache) {
                setLoading(true);
            }

            try {
                // Critical path — do not wait on accessories (was 15–20s)
                const core = await fetchCoreHome();
                if (cancelled) return;

                setSections(core.sections);
                setBanners(core.banners);
                setLoading(false);

                const { items: accessoriesList, types } = await fetchAccessoriesSafe();
                if (cancelled) return;

                setAccessories(accessoriesList);
                setAccessoryTypes(types);
                setCacheEntry(HOME_CACHE_KEY, {
                    sections: core.sections,
                    banners: core.banners,
                    accessories: accessoriesList,
                    accessoryTypes: types,
                });
            } catch (err) {
                console.error('Error fetching home data:', err);
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, []);

    // After CMS sections paint, finish scroll restore (browser back to homepage)
    useEffect(() => {
        if (loading) return undefined;

        let cancelLayoutRestore = () => {};
        let raf2 = 0;

        const raf1 = requestAnimationFrame(() => {
            raf2 = requestAnimationFrame(() => {
                cancelLayoutRestore = flushPendingScrollRestore('/') || (() => {});
            });
        });

        return () => {
            cancelAnimationFrame(raf1);
            cancelAnimationFrame(raf2);
            cancelLayoutRestore();
        };
    }, [loading, sections.length, banners.length, accessories.length]);

    return { sections, banners, accessories, accessoryTypes, loading };
}
